import {
  FormEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import type { Session } from "@supabase/supabase-js";
import {
  Activity,
  AlertTriangle,
  BriefcaseBusiness,
  CheckCircle2,
  Database,
  Download,
  FileText,
  Languages,
  Lightbulb,
  Lock,
  LogOut,
  MapPin,
  RefreshCw,
  Settings,
  ShieldCheck,
  Trash2,
  TrendingUp,
  Users,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type {
  DashboardData,
  Language,
  PikbDashboardData,
  StoredPikbSubmission,
  SurveyKind,
} from "../types";
import { getOptionLabel } from "../data/survey";
import { getPikbCategoryLabel, getPikbZoneLabel } from "../data/pikb";
import {
  completionRate,
  countBy,
  dailySeries,
  exportCsv,
  exportPikbCsv,
  percent,
  uniqueStartedSessions,
} from "../lib/analytics";
import { formatServiceError } from "../lib/errors";
import {
  deletePikbSubmission,
  getCurrentSession,
  getDashboardData,
  getPikbDashboardData,
  isSupabaseConfigured,
  pikbTarget,
  signIn,
  signOut,
  supabase,
  surveyTarget,
} from "../lib/supabase";

type Props = {
  language: Language;
  navigate: (path: string) => void;
  setLanguage: (language: Language) => void;
};

const chartColors = ["#246b57", "#e3a12f", "#3f74b5", "#c45a3d", "#7156a8"];

const text = {
  title: {
    ms: "Pemantauan Respons",
    en: "Response Monitoring",
  },
  subtitle: {
    ms: "Pilih survey untuk pantau kemajuan kutipan, kadar lengkap, dan corak jawapan.",
    en: "Choose a survey to monitor collection progress, completion rate, and response patterns.",
  },
  loginTitle: {
    ms: "Log masuk dashboard",
    en: "Dashboard login",
  },
  loginSubtitle: {
    ms: "Gunakan akaun Supabase Auth yang disediakan untuk pentadbir.",
    en: "Use the Supabase Auth account prepared for administrators.",
  },
  email: { ms: "E-mel", en: "Email" },
  password: { ms: "Kata laluan", en: "Password" },
  signIn: { ms: "Log Masuk", en: "Sign In" },
  total: { ms: "Jumlah respons", en: "Total responses" },
  completion: { ms: "Kadar lengkap", en: "Completion rate" },
  target: { ms: "Kemajuan sasaran", en: "Target progress" },
  today: { ms: "Respons hari ini", en: "Today responses" },
  number: { ms: "No.", en: "No." },
  date: { ms: "Tarikh", en: "Date" },
  issue: { ms: "Isu", en: "Issue" },
  suggestion: { ms: "Cadangan", en: "Suggestion" },
  actions: { ms: "Tindakan", en: "Actions" },
  survey: { ms: "Survey", en: "Survey" },
  putatan: { ms: "Putatan", en: "Putatan" },
  pikb: { ms: "PIKB 2026", en: "PIKB 2026" },
  daily: { ms: "Trend harian", en: "Daily trend" },
  mukim: { ms: "Respons mengikut mukim", en: "Responses by subdistrict" },
  category: { ms: "Penyertaan mengikut kategori", en: "Entries by category" },
  zone: { ms: "Penyertaan mengikut zon", en: "Entries by zone" },
  memberSize: { ms: "Saiz kumpulan", en: "Group size" },
  leader: { ms: "Ketua kumpulan", en: "Group leader" },
  members: { ms: "Ahli", en: "Members" },
  innovation: { ms: "Inovasi", en: "Innovation" },
  summary: { ms: "Penjelasan", en: "Explanation" },
  kampung: { ms: "Nama kampung", en: "Kampung name" },
  mukimColumn: { ms: "Mukim", en: "Subdistrict" },
  age: { ms: "Kumpulan umur", en: "Age groups" },
  employment: { ms: "Status pekerjaan", en: "Employment status" },
  recent: { ms: "Respons terkini", en: "Recent responses" },
  settings: { ms: "Tetapan operasi", en: "Operating settings" },
  export: { ms: "Eksport CSV", en: "Export CSV" },
  refresh: { ms: "Muat semula", en: "Refresh" },
  signOut: { ms: "Log keluar", en: "Sign out" },
  openPutatan: { ms: "Buka Borang Putatan", en: "Open Putatan Form" },
  openPikb: { ms: "Buka Borang PIKB", en: "Open PIKB Form" },
  database: { ms: "Pangkalan data", en: "Database" },
  protected: { ms: "Dilindungi Auth", en: "Auth protected" },
  open: { ms: "Terbuka kepada pautan awam", en: "Open to public link" },
  preview: {
    ms: "Pemboleh ubah Supabase belum ditetapkan. Dashboard ini menggunakan data pratonton tempatan.",
    en: "Supabase environment variables are not set. This dashboard is using local preview data.",
  },
  starts: { ms: "sesi mula direkod", en: "starts tracked" },
  delete: { ms: "Padam", en: "Delete" },
  keepResponse: { ms: "Kekalkan Respons", en: "Keep Response" },
  deleteResponse: { ms: "Padam Respons", en: "Delete Response" },
  deleting: { ms: "Memadam...", en: "Deleting..." },
  deleteTitle: {
    ms: "Padam respons PIKB?",
    en: "Delete PIKB response?",
  },
  deleteBody: {
    ms: "Respons ini akan dipadam daripada dashboard dan eksport CSV. Tindakan ini tidak boleh dibuat asal.",
    en: "This response will be removed from the dashboard and CSV export. This action cannot be undone.",
  },
  deleteSuccess: {
    ms: "Respons PIKB telah dipadam.",
    en: "PIKB response deleted.",
  },
};

export default function DashboardPage({
  language,
  navigate,
  setLanguage,
}: Props) {
  const [selectedSurvey, setSelectedSurvey] =
    useState<SurveyKind>("putatan");
  const [session, setSession] = useState<Session | null>(null);
  const [authLoading, setAuthLoading] = useState(isSupabaseConfigured);
  const [data, setData] = useState<DashboardData | PikbDashboardData | null>(
    null,
  );
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState("");
  const [pendingDelete, setPendingDelete] =
    useState<StoredPikbSubmission | null>(null);
  const [deletingId, setDeletingId] = useState("");

  useEffect(() => {
    if (!isSupabaseConfigured) {
      setAuthLoading(false);
      return;
    }

    void getCurrentSession().then((currentSession) => {
      setSession(currentSession);
      setAuthLoading(false);
    });

    const subscription = supabase?.auth.onAuthStateChange((_event, current) => {
      setSession(current);
    });

    return () => {
      subscription?.data.subscription.unsubscribe();
    };
  }, []);

  const loadData = async (survey: SurveyKind = selectedSurvey) => {
    setLoadingData(true);
    setError("");
    try {
      const nextData =
        survey === "pikb" ? await getPikbDashboardData() : await getDashboardData();
      setData(nextData);
    } catch (loadError) {
      setError(
        formatServiceError(
          loadError,
          language === "ms"
            ? "Data dashboard tidak dapat dimuat. Sila cuba lagi."
            : "Unable to load dashboard data. Please try again.",
        ),
      );
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    if (!isSupabaseConfigured || session) {
      void loadData();
    }
  }, [selectedSurvey, session]);

  const handleSurveyChange = (survey: SurveyKind) => {
    if (survey === selectedSurvey) return;
    setSelectedSurvey(survey);
    setData(null);
    setPendingDelete(null);
  };

  const handleDeletePikb = async () => {
    if (!pendingDelete) return;

    setDeletingId(pendingDelete.id);
    setError("");
    try {
      await deletePikbSubmission(pendingDelete.id);
      setData((current) => {
        if (!current || selectedSurvey !== "pikb") return current;
        return {
          ...current,
          submissions: (current as PikbDashboardData).submissions.filter(
            (submission) => submission.id !== pendingDelete.id,
          ),
        } as PikbDashboardData;
      });
      setPendingDelete(null);
    } catch (deleteError) {
      setError(
        formatServiceError(
          deleteError,
          language === "ms"
            ? "Respons PIKB tidak dapat dipadam. Sila cuba lagi."
            : "Unable to delete PIKB response. Please try again.",
        ),
      );
    } finally {
      setDeletingId("");
    }
  };

  const currentTarget =
    selectedSurvey === "pikb" ? pikbTarget : surveyTarget;
  const headingEyebrow =
    selectedSurvey === "pikb" ? "PIKB 2026 Ops" : "Putatan Survey Ops";

  if (authLoading) {
    return (
      <main className="dashboard-page">
        <div className="loading-panel">Loading dashboard...</div>
      </main>
    );
  }

  if (isSupabaseConfigured && !session) {
    return <LoginPanel language={language} setLanguage={setLanguage} />;
  }

  return (
    <main className="dashboard-page">
      <section className="dashboard-heading">
        <div>
          <span className="eyebrow">{headingEyebrow}</span>
          <h1>{text.title[language]}</h1>
          <p>{text.subtitle[language]}</p>
        </div>
        <div className="dashboard-actions">
          <div className="survey-switcher" aria-label={text.survey[language]}>
            <button
              className={selectedSurvey === "putatan" ? "is-active" : ""}
              type="button"
              onClick={() => handleSurveyChange("putatan")}
            >
              <FileText size={17} />
              {text.putatan[language]}
            </button>
            <button
              className={selectedSurvey === "pikb" ? "is-active" : ""}
              type="button"
              onClick={() => handleSurveyChange("pikb")}
            >
              <Lightbulb size={17} />
              {text.pikb[language]}
            </button>
          </div>
          <button
            className="secondary-button"
            type="button"
            onClick={() => navigate("/putatan")}
          >
            <FileText size={17} />
            {text.openPutatan[language]}
          </button>
          <button
            className="secondary-button"
            type="button"
            onClick={() => navigate("/pikb")}
          >
            <Lightbulb size={17} />
            {text.openPikb[language]}
          </button>
          <button
            className="secondary-button"
            onClick={() => loadData()}
            disabled={loadingData}
            title={text.refresh[language]}
          >
            <RefreshCw size={18} />
            {text.refresh[language]}
          </button>
          <button
            className="primary-button"
            disabled={!data?.submissions.length}
            onClick={() => {
              if (!data) return;
              if (selectedSurvey === "pikb") {
                exportPikbCsv((data as PikbDashboardData).submissions);
              } else {
                exportCsv((data as DashboardData).submissions);
              }
            }}
          >
            <Download size={18} />
            {text.export[language]}
          </button>
          {isSupabaseConfigured ? (
            <button
              className="icon-only"
              onClick={() => void signOut()}
              title={text.signOut[language]}
              aria-label={text.signOut[language]}
            >
              <LogOut size={19} />
            </button>
          ) : null}
          <button
            className="icon-only"
            type="button"
            onClick={() => setLanguage(language === "ms" ? "en" : "ms")}
            title="Switch language"
            aria-label="Switch language"
          >
            <Languages size={18} />
          </button>
        </div>
      </section>

      {error ? <div className="submit-error">{error}</div> : null}

      {data ? (
        selectedSurvey === "pikb" ? (
          <PikbDashboardContent
            data={data as PikbDashboardData}
            language={language}
            target={currentTarget}
            deletingId={deletingId}
            onDeleteRequest={setPendingDelete}
          />
        ) : (
          <DashboardContent
            data={data as DashboardData}
            language={language}
            target={currentTarget}
          />
        )
      ) : (
        <div className="loading-panel">Loading dashboard...</div>
      )}

      {pendingDelete ? (
        <DeletePikbDialog
          language={language}
          submission={pendingDelete}
          isDeleting={deletingId === pendingDelete.id}
          onCancel={() => {
            if (!deletingId) setPendingDelete(null);
          }}
          onConfirm={() => void handleDeletePikb()}
        />
      ) : null}
    </main>
  );
}

function LoginPanel({
  language,
  setLanguage,
}: {
  language: Language;
  setLanguage: (language: Language) => void;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      await signIn(email, password);
    } catch (signInError) {
      setError(
        formatServiceError(
          signInError,
          language === "ms"
            ? "Tidak dapat log masuk. Sila cuba lagi."
            : "Unable to sign in. Please try again.",
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="dashboard-page login-page">
      <form className="login-card" onSubmit={handleSubmit}>
        <button
          className="icon-only login-language-button"
          type="button"
          onClick={() => setLanguage(language === "ms" ? "en" : "ms")}
          title="Switch language"
          aria-label="Switch language"
        >
          <Languages size={18} />
        </button>
        <span className="lock-icon">
          <Lock size={24} />
        </span>
        <h1>{text.loginTitle[language]}</h1>
        <p>{text.loginSubtitle[language]}</p>

        <label>
          {text.email[language]}
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="email"
            required
          />
        </label>
        <label>
          {text.password[language]}
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            required
          />
        </label>

        {error ? <p className="field-error">{error}</p> : null}

        <button className="primary-button" disabled={loading}>
          <ShieldCheck size={18} />
          {loading ? "..." : text.signIn[language]}
        </button>
      </form>
    </main>
  );
}

function DashboardContent({
  data,
  language,
  target,
}: {
  data: DashboardData;
  language: Language;
  target: number;
}) {
  const starts = uniqueStartedSessions(data.events);
  const rate = completionRate(data);
  const targetRate = percent(data.submissions.length, target);
  const today = new Date().toISOString().slice(0, 10);
  const todayCount = data.submissions.filter((submission) =>
    submission.created_at.startsWith(today),
  ).length;

  const daily = useMemo(() => dailySeries(data), [data]);
  const byMukim = useMemo(
    () => countBy(data.submissions, "mukim", language),
    [data, language],
  );
  const byAge = useMemo(
    () => countBy(data.submissions, "age_group", language),
    [data, language],
  );
  const byEmployment = useMemo(
    () => countBy(data.submissions, "employment_status", language),
    [data, language],
  );

  return (
    <>
      {!isSupabaseConfigured ? (
        <div className="config-banner">
          <Database size={19} />
          <span>{text.preview[language]}</span>
        </div>
      ) : null}

      <section className="metric-grid">
        <Metric
          icon={<Users size={22} />}
          label={text.total[language]}
          value={data.submissions.length.toLocaleString()}
          hint={`${starts || data.submissions.length} ${text.starts[language]}`}
        />
        <Metric
          icon={<CheckCircle2 size={22} />}
          label={text.completion[language]}
          value={`${rate}%`}
          hint={language === "ms" ? "berdasarkan sesi mula" : "from started sessions"}
        />
        <Metric
          icon={<TrendingUp size={22} />}
          label={text.target[language]}
          value={`${targetRate}%`}
          hint={`${data.submissions.length}/${target}`}
        />
        <Metric
          icon={<Activity size={22} />}
          label={text.today[language]}
          value={todayCount.toLocaleString()}
          hint={new Date().toLocaleDateString()}
        />
      </section>

      <section className="dashboard-grid">
        <Panel title={text.daily[language]} icon={<Activity size={19} />}>
          <div className="chart-box">
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={daily}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="starts"
                  stroke="#3f74b5"
                  strokeWidth={3}
                  dot={{ r: 3 }}
                />
                <Line
                  type="monotone"
                  dataKey="submissions"
                  stroke="#246b57"
                  strokeWidth={3}
                  dot={{ r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title={text.mukim[language]} icon={<MapPin size={19} />}>
          <div className="chart-box">
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={byMukim}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={56}
                  outerRadius={92}
                  paddingAngle={3}
                >
                  {byMukim.map((entry, index) => (
                    <Cell
                      key={entry.name}
                      fill={chartColors[index % chartColors.length]}
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <LegendList items={byMukim} />
        </Panel>

        <Panel title={text.age[language]} icon={<Users size={19} />}>
          <div className="chart-box">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={byAge}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="value" radius={[8, 8, 0, 0]} fill="#e3a12f" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title={text.employment[language]} icon={<BriefcaseBusiness size={19} />}>
          <LegendList items={byEmployment} />
        </Panel>
      </section>

      <section className="dashboard-grid lower">
        <Panel title={text.recent[language]} icon={<Database size={19} />}>
          <div className="response-table-wrap">
            <table className="response-table">
              <thead>
                <tr>
                  <th className="response-number-column">{text.number[language]}</th>
                  <th>{text.date[language]}</th>
                  <th>{text.kampung[language]}</th>
                  <th>{text.mukimColumn[language]}</th>
                  <th>{text.issue[language]}</th>
                  <th>{text.suggestion[language]}</th>
                </tr>
              </thead>
              <tbody>
                {data.submissions.slice(0, 8).map((submission, index) => (
                  <tr key={submission.id}>
                    <td className="response-number-column">{index + 1}</td>
                    <td>{new Date(submission.created_at).toLocaleDateString()}</td>
                    <td>
                      {submission.nama_kampung
                        ? getOptionLabel("nama_kampung", submission.nama_kampung, language)
                        : "—"}
                    </td>
                    <td>{getOptionLabel("mukim", submission.mukim, language)}</td>
                    <td>{submission.main_issue}</td>
                    <td>{submission.development_suggestion}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>

        <Panel title={text.settings[language]} icon={<Settings size={19} />}>
          <div className="settings-list">
            <SettingRow
              label={text.database[language]}
              value={isSupabaseConfigured ? "Supabase connected" : "Local preview"}
            />
            <SettingRow label="Survey status" value={text.open[language]} />
            <SettingRow label="Target responses" value={String(target)} />
            <SettingRow label="Dashboard" value={text.protected[language]} />
            <SettingRow
              label="Public survey"
              value={language === "ms" ? "Tidak perlu log masuk" : "No login required"}
            />
          </div>
        </Panel>
      </section>
    </>
  );
}

function PikbDashboardContent({
  data,
  language,
  target,
  deletingId,
  onDeleteRequest,
}: {
  data: PikbDashboardData;
  language: Language;
  target: number;
  deletingId: string;
  onDeleteRequest: (submission: StoredPikbSubmission) => void;
}) {
  const starts = uniqueStartedSessions(data.events);
  const rate = completionRate(data);
  const targetRate = percent(data.submissions.length, target);
  const today = new Date().toISOString().slice(0, 10);
  const todayCount = data.submissions.filter((submission) =>
    submission.created_at.startsWith(today),
  ).length;
  const totalMembers = data.submissions.reduce(
    (sum, submission) => sum + submission.members.length,
    0,
  );

  const daily = useMemo(() => dailySeries(data), [data]);
  const byZone = useMemo(() => {
    const map = new Map<string, number>();
    data.submissions.forEach((submission) => {
      const zone = submission.zone || "beaufort";
      map.set(zone, (map.get(zone) ?? 0) + 1);
    });

    return Array.from(map.entries())
      .map(([name, value]) => ({
        name: getPikbZoneLabel(name, language),
        value,
      }))
      .sort((a, b) => b.value - a.value);
  }, [data.submissions, language]);
  const byCategory = useMemo(() => {
    const map = new Map<string, number>();
    data.submissions.forEach((submission) => {
      map.set(submission.category, (map.get(submission.category) ?? 0) + 1);
    });

    return Array.from(map.entries())
      .map(([name, value]) => ({
        name: getPikbCategoryLabel(name, language),
        value,
      }))
      .sort((a, b) => b.value - a.value);
  }, [data.submissions, language]);
  const byGroupSize = useMemo(() => {
    const map = new Map<string, number>();
    data.submissions.forEach((submission) => {
      const size = submission.members.length + 1;
      const label =
        language === "ms"
          ? `${size} orang`
          : `${size} ${size === 1 ? "person" : "people"}`;
      map.set(label, (map.get(label) ?? 0) + 1);
    });

    return Array.from(map.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [data.submissions, language]);

  return (
    <>
      {!isSupabaseConfigured ? (
        <div className="config-banner">
          <Database size={19} />
          <span>{text.preview[language]}</span>
        </div>
      ) : null}

      <section className="metric-grid">
        <Metric
          icon={<Lightbulb size={22} />}
          label={text.total[language]}
          value={data.submissions.length.toLocaleString()}
          hint={`${starts || data.submissions.length} ${text.starts[language]}`}
        />
        <Metric
          icon={<CheckCircle2 size={22} />}
          label={text.completion[language]}
          value={`${rate}%`}
          hint={language === "ms" ? "berdasarkan sesi mula" : "from started sessions"}
        />
        <Metric
          icon={<TrendingUp size={22} />}
          label={text.target[language]}
          value={`${targetRate}%`}
          hint={`${data.submissions.length}/${target}`}
        />
        <Metric
          icon={<Users size={22} />}
          label={text.members[language]}
          value={totalMembers.toLocaleString()}
          hint={language === "ms" ? `${todayCount} hari ini` : `${todayCount} today`}
        />
      </section>

      <section className="dashboard-grid">
        <Panel title={text.daily[language]} icon={<Activity size={19} />}>
          <div className="chart-box">
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={daily}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="starts"
                  stroke="#3f74b5"
                  strokeWidth={3}
                  dot={{ r: 3 }}
                />
                <Line
                  type="monotone"
                  dataKey="submissions"
                  stroke="#246b57"
                  strokeWidth={3}
                  dot={{ r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title={text.zone[language]} icon={<MapPin size={19} />}>
          <div className="chart-box">
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={byZone}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={56}
                  outerRadius={92}
                  paddingAngle={3}
                >
                  {byZone.map((entry, index) => (
                    <Cell
                      key={entry.name}
                      fill={chartColors[index % chartColors.length]}
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <LegendList items={byZone} />
        </Panel>

        <Panel title={text.category[language]} icon={<Lightbulb size={19} />}>
          <div className="chart-box">
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={byCategory}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={56}
                  outerRadius={92}
                  paddingAngle={3}
                >
                  {byCategory.map((entry, index) => (
                    <Cell
                      key={entry.name}
                      fill={chartColors[index % chartColors.length]}
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <LegendList items={byCategory} />
        </Panel>
      </section>

      <section className="dashboard-grid lower">
        <Panel title={text.memberSize[language]} icon={<Users size={19} />}>
          <div className="chart-box">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={byGroupSize}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="value" radius={[8, 8, 0, 0]} fill="#e3a12f" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title={text.settings[language]} icon={<Settings size={19} />}>
          <div className="settings-list">
            <SettingRow
              label={text.database[language]}
              value={isSupabaseConfigured ? "Supabase connected" : "Local preview"}
            />
            <SettingRow label="Survey status" value={text.open[language]} />
            <SettingRow label="Target responses" value={String(target)} />
            <SettingRow label="Dashboard" value={text.protected[language]} />
            <SettingRow
              label="Public survey"
              value={language === "ms" ? "Tidak perlu log masuk" : "No login required"}
            />
          </div>
        </Panel>
      </section>

      <section className="dashboard-grid single">
        <Panel title={text.recent[language]} icon={<Database size={19} />}>
          <div className="response-table-wrap">
            <table className="response-table pikb-response-table">
              <thead>
                <tr>
                  <th className="response-number-column">{text.number[language]}</th>
                  <th>{text.date[language]}</th>
                  <th>{text.zone[language]}</th>
                  <th>{text.category[language]}</th>
                  <th>{text.leader[language]}</th>
                  <th>{text.members[language]}</th>
                  <th>{text.innovation[language]}</th>
                  <th>{text.summary[language]}</th>
                  <th>{text.actions[language]}</th>
                </tr>
              </thead>
              <tbody>
                {data.submissions.slice(0, 8).map((submission, index) => (
                  <tr key={submission.id}>
                    <td className="response-number-column">{index + 1}</td>
                    <td>{new Date(submission.created_at).toLocaleDateString()}</td>
                    <td>{getPikbZoneLabel(submission.zone || "beaufort", language)}</td>
                    <td>{getPikbCategoryLabel(submission.category, language)}</td>
                    <td>{submission.leader_name}</td>
                    <td>
                      {submission.members.length
                        ? submission.members.map((member) => member.name).join(", ")
                        : "—"}
                    </td>
                    <td>{submission.innovation_title}</td>
                    <td>{submission.innovation_summary}</td>
                    <td>
                      <button
                        className="icon-only danger-button"
                        type="button"
                        title={`${text.delete[language]} ${submission.leader_name}`}
                        aria-label={`${text.delete[language]} ${submission.leader_name}`}
                        disabled={Boolean(deletingId)}
                        onClick={() => onDeleteRequest(submission)}
                      >
                        <Trash2 size={17} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      </section>
    </>
  );
}

function DeletePikbDialog({
  language,
  submission,
  isDeleting,
  onCancel,
  onConfirm,
}: {
  language: Language;
  submission: StoredPikbSubmission;
  isDeleting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      if (!isDeleting) onCancel();
      return;
    }

    if (event.key !== "Tab") return;

    const focusable = Array.from(
      dialogRef.current?.querySelectorAll<HTMLElement>(
        'button:not(:disabled), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      ) ?? [],
    );
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <div className="modal-overlay delete-overlay" onKeyDown={handleKeyDown}>
      <div
        className="delete-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="delete-pikb-title"
        aria-describedby="delete-pikb-description"
        ref={dialogRef}
      >
        <span className="delete-dialog-icon">
          <AlertTriangle size={24} />
        </span>
        <div>
          <h2 id="delete-pikb-title">{text.deleteTitle[language]}</h2>
          <p id="delete-pikb-description">{text.deleteBody[language]}</p>
          <dl className="delete-response-summary">
            <div>
              <dt>{text.leader[language]}</dt>
              <dd>{submission.leader_name}</dd>
            </div>
            <div>
              <dt>{text.innovation[language]}</dt>
              <dd>{submission.innovation_title}</dd>
            </div>
          </dl>
        </div>
        <div className="delete-dialog-actions">
          <button
            className="primary-button"
            type="button"
            ref={cancelRef}
            disabled={isDeleting}
            onClick={onCancel}
          >
            {text.keepResponse[language]}
          </button>
          <button
            className="danger-action-button"
            type="button"
            disabled={isDeleting}
            onClick={onConfirm}
          >
            <Trash2 size={17} />
            {isDeleting ? text.deleting[language] : text.deleteResponse[language]}
          </button>
        </div>
      </div>
    </div>
  );
}

function Metric({
  icon,
  label,
  value,
  hint,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  hint: string;
}) {
  return (
    <article className="metric-card">
      <span className="metric-icon">{icon}</span>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{hint}</small>
      </div>
    </article>
  );
}

function Panel({
  title,
  icon,
  children,
}: {
  title: string;
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <article className="dashboard-panel">
      <header>
        <span>{icon}</span>
        <h2>{title}</h2>
      </header>
      {children}
    </article>
  );
}

function LegendList({ items }: { items: Array<{ name: string; value: number }> }) {
  return (
    <div className="legend-list">
      {items.map((item, index) => (
        <div key={item.name}>
          <span
            style={{ background: chartColors[index % chartColors.length] }}
          />
          <strong>{item.name}</strong>
          <em>{item.value}</em>
        </div>
      ))}
    </div>
  );
}

function SettingRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="setting-row">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}
